// Third Party
import React, { Component } from 'react';
import { SortingRule } from 'react-table';

// Components
import DelegateBanner from '../auth/DelegateBanner';
import { AnnouncementBanner } from '../announcements';
import DefaultNewComparison from './NewComparison/LegacyDefaultNewComparison';
import LinkNewComparison from './NewComparison/LegacyLinkNewComparison';

// Enums
import { HoldingAllocationMode } from '@/enums/Portfolio';

// Types
import { SortingField } from '../../../types/data';

// Utils
import heapUtils, { HEAP_CUSTOM_EVENTS } from '../../../utils/3rd-party/heap';
import { formatMessage } from '../../../utils/i18n';
import { LoadModelsModal, ContactPortfoliosModal, EditPortfolioModal } from '../../components/modals';
import { getContactLayout } from '../../../utils/contacts';
import disclaimers from '../../../utils/disclaimers';
import { clonePortfolioAsProxy } from '../../../utils/portfolio/utils';
import { userHasPermissionEnabled, PERMISSIONS } from '../../../utils/permissions';
import { formatMessageWithLocale } from '../../../utils/i18n/i18n';
import User from '@/types/user';
import GenericContact from '@/types/generic-contact';
import Portfolio from '@/types/portfolio';
import ComparisonItem from '@/types/comparison-item';
import Locale from '@/types/locale';
import AppMode from '@/types/app-mode';
import PortfolioUploadModal from '@/views/components/modals/portfolio-upload/PortfolioUploadModal';
import { togglePortfolioEditor } from '../../../redux/modules/common/actionCreators';
import { ReduxState } from '@/redux/modules/types';
import { connect } from 'react-redux';

type DispatchProps = {
  togglePortfolioEditor: (show: boolean) => void;
};

type StateProps = {
  editPortfolioModalIsVisible: boolean;
};

type Props = DispatchProps &
  StateProps & {
    queries: {
      contactPortfolios: string;
    };
    user: User;
    selectedContact?: {
      contact: GenericContact;
      portfolios?: Portfolio[];
      hasMore: {
        portfolios: boolean;
      };
    };
    isComparingPortfolios: boolean;
    locale: Locale;
    onCloseComparison: () => void;
    addPortfoliosToComparison: (portfolios: Portfolio[]) => Promise<void>;
    onSearchContactPortfolios: (query: string) => void;
    resetRoute: () => void;
    loadContactPortfolios: (contact: GenericContact) => Promise<void>;
    onLoadMoreContactPortfolios: (sort?: SortingField[]) => void;
    resetCurrentContact: () => void;
    savePortfolio: (portfolio: Portfolio, contact?: GenericContact) => Promise<Portfolio>;
    resetPortfolio: () => void;
    loadNewPortfolio: () => void;
    loadPortfolioForEdit: (portfolio: Portfolio, comparisonItem?: ComparisonItem, isPinned?: boolean) => Promise<void>;
    getComparisonContact: () => Promise<GenericContact | undefined>;
    setPortfolioContact: (contact: GenericContact) => void;
  };

type State = {
  showModelPortfoliosModal: boolean;
  showContactPortfoliosModal: boolean;
  showPortfolioUploadModal: boolean;
  pendingPortfolios: Portfolio[];
  sorting: SortingRule[];
};

class EmptyComparison extends Component<Props, State> {
  readonly state: State = {
    showModelPortfoliosModal: false,
    showContactPortfoliosModal: false,
    showPortfolioUploadModal: false,
    pendingPortfolios: [],
    sorting: [],
  };

  componentDidMount() {
    this.props.resetRoute();
  }

  addPortfoliosToComparison = (portfolios: Portfolio[]) => {
    if (portfolios.some(p => disclaimers.portfolioHasMissingOrInvalidHoldings(p))) {
      this.setState({ pendingPortfolios: portfolios }, () => {
        this.hideVisibleModal();
        this.handlePendingPortfolios();
      });

      return Promise.resolve();
    }

    return this.props.addPortfoliosToComparison(portfolios);
  };

  handlePendingPortfolios = () => {
    const { user } = this.props;
    const { pendingPortfolios } = this.state;
    if (pendingPortfolios && pendingPortfolios.length) {
      const portfolioWithMissingHolding = pendingPortfolios.find(p => disclaimers.portfolioHasMissingOrInvalidHoldings(p));
      if (portfolioWithMissingHolding) {
        pendingPortfolios.splice(pendingPortfolios.indexOf(portfolioWithMissingHolding), 1);
        const portfolioClone = clonePortfolioAsProxy(portfolioWithMissingHolding);

        if (user && userHasPermissionEnabled(user, PERMISSIONS.PORTFOLIO_UNITS_MODE)) {
          portfolioClone.allocationMode = HoldingAllocationMode.Units;
        }

        this.showEditPortfolioModal(portfolioClone);
      } else {
        this.setState({ pendingPortfolios: [] });
        this.addPortfoliosToComparison(pendingPortfolios);
      }
    }
  };

  hideVisibleModal = () => {
    const { showModelPortfoliosModal, showContactPortfoliosModal } = this.state;
    if (showModelPortfoliosModal === true) {
      this.hideModelPortfoliosModal();
    } else if (showContactPortfoliosModal === true) {
      this.hideContactPortfoliosModal();
    }
  };

  hideModelPortfoliosModal = () => this.setState({ showModelPortfoliosModal: false });

  showModelPortfoliosModal = () => this.setState({ showModelPortfoliosModal: true });

  hideContactPortfoliosModal = () => this.setState({ showContactPortfoliosModal: false });

  showContactPortfoliosModal = () => this.setState({ showContactPortfoliosModal: true });

  hideEditPortfolioModal = () => {
    this.props.resetPortfolio();
    this.props.togglePortfolioEditor(false);
  };

  showEditPortfolioModal = async (portfolio?: Portfolio) => {
    const contact = await this.props.getComparisonContact();

    this.props.resetPortfolio();

    if (portfolio?._id === undefined) {
      this.props.loadNewPortfolio();
    } else {
      this.props.loadPortfolioForEdit(portfolio);
    }

    if (contact) {
      this.props.setPortfolioContact(contact);
    }

    this.props.togglePortfolioEditor(true);
  };

  showPortfolioUploadModal = () => this.setState({ showPortfolioUploadModal: true });

  hidePortfolioUploadModal = () => this.setState({ showPortfolioUploadModal: false });

  savePortfolio = (newPortfolio: Portfolio): Promise<Portfolio> => {
    const id = newPortfolio._id;
    return this.props.savePortfolio(newPortfolio).then(portfolio => {
      heapUtils.track(HEAP_CUSTOM_EVENTS.NEW_COMPARISON);
      const { pendingPortfolios } = this.state;
      if (pendingPortfolios.length) {
        this.setState({ pendingPortfolios: pendingPortfolios.filter(p => p._id !== id).concat([portfolio]) }, () => {
          this.hideEditPortfolioModal();
        });
      } else {
        this.hideEditPortfolioModal();
        this.props.addPortfoliosToComparison([portfolio]);
      }

      return portfolio;
    });
  };

  cancelPortfolioEdit = () => {
    this.hideEditPortfolioModal();
  };

  renderDivider = () => (
    <div className="divider-with-label">
      <div className="line-container" aria-hidden>
        <div className="line" />
      </div>
      <div className="label-container">
        <span className="label">{formatMessageWithLocale('LinkEmptyComparison.OrStartWith')}</span>
      </div>
    </div>
  );

  render() {
    const { queries, user, selectedContact, isComparingPortfolios, editPortfolioModalIsVisible } = this.props;
    const { showModelPortfoliosModal, showContactPortfoliosModal } = this.state;

    const isLinkMode = user.appConfig.mode === AppMode.LINK;

    return (
      <>
        <DelegateBanner />
        <AnnouncementBanner />

        {isLinkMode ? (
          <LinkNewComparison user={user} showEditPortfolioModal={this.showEditPortfolioModal} showPortfolioUploadModal={this.showPortfolioUploadModal} />
        ) : (
          <DefaultNewComparison
            user={user}
            onCloseComparison={this.props.onCloseComparison}
            showContactPortfoliosModal={this.showContactPortfoliosModal}
            showModelPortfoliosModal={this.showModelPortfoliosModal}
            showEditPortfolioModal={this.showEditPortfolioModal}
            showPortfolioUploadModal={this.showPortfolioUploadModal}
          />
        )}

        {showModelPortfoliosModal && (
          <LoadModelsModal
            user={user}
            show={showModelPortfoliosModal}
            onCancel={this.hideModelPortfoliosModal}
            addPortfoliosToComparison={this.addPortfoliosToComparison}
          />
        )}

        {showContactPortfoliosModal && (
          <ContactPortfoliosModal
            isComparing={isComparingPortfolios}
            query={queries.contactPortfolios}
            show={showContactPortfoliosModal}
            selectedContact={selectedContact}
            onCancel={this.hideContactPortfoliosModal}
            onSearchContactPortfolios={this.props.onSearchContactPortfolios}
            loadContactPortfolios={this.props.loadContactPortfolios}
            loadMoreContactPortfolios={this.props.onLoadMoreContactPortfolios}
            addPortfoliosToComparison={this.addPortfoliosToComparison}
            resetCurrentContact={this.props.resetCurrentContact}
            layout={getContactLayout(user)}
          />
        )}

        <EditPortfolioModal
          show={editPortfolioModalIsVisible}
          onSave={this.savePortfolio}
          onCancel={this.cancelPortfolioEdit}
          saveLabel={formatMessage('Comparison.AddToComparison')}
          saveNotification={formatMessage('HoldingsEditor.Created.PORTFOLIO')}
          onExited={this.handlePendingPortfolios}
        />

        <PortfolioUploadModal
          show={this.state.showPortfolioUploadModal}
          onCancel={this.hidePortfolioUploadModal}
          titleId="Modal.PortfolioUpload.UploadClientPortfolio"
        />
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState): StateProps => ({
  editPortfolioModalIsVisible: state.comparison.portfolioEditor.isVisible,
});

export default connect(mapStateToProps, {
  togglePortfolioEditor,
})(EmptyComparison);
